import { useCallback, useMemo } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useOperationsDocumentUpload } from "@features/vessel-call/vessel-call-status/Operations/hooks/useOperationsDocumentUpload";
import { useReviewDisputeResolution } from "@features/vessel-call/vessel-call-status/Operations/hooks/useReviewDisputeResolution";
import {
  getReviewResolutionDrawerSchema,
  REVIEW_RESOLUTION_DETAILS_MAX_LENGTH,
  REVIEW_RESOLUTION_DOCUMENT_CERTIFICATE_TYPE,
  REVIEW_RESOLUTION_DOCUMENT_FIELD,
  REVIEW_RESOLUTION_DOCUMENTS_MAX_FILES,
} from "@features/vessel-call/vessel-call-status/Operations/schema/reviewResolutionDrawerSchema";
import type { ReviewResolutionDrawerFormValues } from "@features/vessel-call/vessel-call-status/Operations/types/types";
import { zodResolver } from "@hookform/resolvers/zod";
import type { DisputeDocument } from "@services/disputes/types";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export type ReviewResolutionMode = "accept" | "reject";

type ReviewResolutionDrawerProps = {
  disputeId: string | null;
  isOpen: boolean;
  mode: ReviewResolutionMode;
  onClose: () => void;
};

const LANG_BASE = "vessel-call.operations";
const TEST_ID_BASE = "operations-review-resolution";

const defaultValues: ReviewResolutionDrawerFormValues = {
  details: "",
  [REVIEW_RESOLUTION_DOCUMENT_FIELD]: [],
};

const ReviewResolutionDrawer = ({
  disputeId,
  isOpen,
  mode,
  onClose,
}: ReviewResolutionDrawerProps) => {
  const { t } = useTranslation();
  const uploadConditions = useFileUploadProfiles().disputeDocument;
  const schema = useMemo(() => getReviewResolutionDrawerSchema(t, mode), [mode, t]);
  const form = useForm<ReviewResolutionDrawerFormValues>({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
    defaultValues,
  });

  const setFormDocuments = useCallback(
    (documents: DisputeDocument[]) => {
      form.setValue(REVIEW_RESOLUTION_DOCUMENT_FIELD, documents, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form],
  );
  const {
    files,
    isUploading,
    setFiles,
    handleUpload,
    validateFiles,
    validateDrop,
    syncDocumentsFromStore,
    resetDocuments,
  } = useOperationsDocumentUpload({
    fieldName: REVIEW_RESOLUTION_DOCUMENT_FIELD,
    certificateType: REVIEW_RESOLUTION_DOCUMENT_CERTIFICATE_TYPE,
    maxFiles: REVIEW_RESOLUTION_DOCUMENTS_MAX_FILES,
    onDocumentsChange: setFormDocuments,
  });
  const { reviewResolution, isReviewingResolution } = useReviewDisputeResolution();

  const allowedFileExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((extension) =>
        extension.startsWith(".") ? extension.toLowerCase() : `.${extension.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  const handleClose = useCallback(() => {
    resetDocuments();
    form.reset(defaultValues);
    onClose();
  }, [form, onClose, resetDocuments]);

  const handleSubmit = () => {
    form.handleSubmit((values) => {
      if (!disputeId || isReviewingResolution) return;

      reviewResolution(
        {
          disputeId,
          payload: {
            action: mode === "accept" ? "ACCEPT" : "REJECT",
            disputeDetails: values.details.trim(),
            documents: values[REVIEW_RESOLUTION_DOCUMENT_FIELD] ?? [],
          },
        },
        { onSuccess: handleClose },
      );
    })();
  };

  const modeKey = mode === "accept" ? "accept" : "reject";

  return (
    <div data-testid={`${TEST_ID_BASE}-${mode}-drawer`}>
      <FormProvider {...form}>
        <SideDrawer
          isOpen={isOpen}
          title={t(`${LANG_BASE}.${modeKey}-resolution-title`)}
          submitLabel={t(`${LANG_BASE}.${modeKey}`)}
          cancelLabel={t("common.cancel")}
          onClose={handleClose}
          onCancel={handleClose}
          onSubmit={handleSubmit}
          isSubmitting={isReviewingResolution}
          isSubmitDisabled={isUploading || isReviewingResolution}
          disableBackdropClose
          keepMounted={false}
          width={370}
        >
          <div className="flex w-[370px] max-w-full flex-col gap-6 p-6">
            <FormItem<ReviewResolutionDrawerFormValues, "details">
              name="details"
              type="textarea"
              label={t(`${LANG_BASE}.${mode === "accept" ? "comments" : "rejection-reason"}`)}
              testId={`${TEST_ID_BASE}-${mode}-details`}
              minRows={4}
              maxChars={REVIEW_RESOLUTION_DETAILS_MAX_LENGTH}
              showCharCounter
            />
            <div className="flex flex-col gap-4">
              <FormItem<ReviewResolutionDrawerFormValues, typeof REVIEW_RESOLUTION_DOCUMENT_FIELD>
                name={REVIEW_RESOLUTION_DOCUMENT_FIELD}
                type="file"
                label={t(`${LANG_BASE}.documents`)}
                testId={`${TEST_ID_BASE}-${mode}-documents`}
                allowedFileExtensions={allowedFileExtensions}
                uploadConditions={uploadConditions}
                maxFiles={REVIEW_RESOLUTION_DOCUMENTS_MAX_FILES}
                files={files}
                setFiles={setFiles}
                uploadDescription={t("upload.additional-documents-upload", {
                  extensions: allowedFileExtensions.join(", "),
                  size: uploadConditions.maxSizeLabel,
                })}
                onUpload={handleUpload}
                fileValidation={validateFiles}
                validateDrop={validateDrop}
                disabled={isUploading}
              />
              {files.map((file, index) => (
                <SingleFile
                  key={`${file.name}-${index}`}
                  testId={`${TEST_ID_BASE}-${mode}-document-${index}`}
                  file={file}
                  fieldName={REVIEW_RESOLUTION_DOCUMENT_FIELD}
                  currentFileIndex={index}
                  setFiles={() => {}}
                  removeFn={syncDocumentsFromStore}
                  isLoading={isUploading && file instanceof File}
                />
              ))}
            </div>
          </div>
        </SideDrawer>
      </FormProvider>
    </div>
  );
};

export default ReviewResolutionDrawer;
